import { useTranslation } from "react-i18next";
import { Badge } from "@/components/ui/badge";
import { OverallScoreRing } from "./review-overall-score-ring";
import type { IEvaluationRequestDetailSummaryMetric } from "@/types/(company)/performance.interface";
import { cn } from "@/utils/cn";
import { Circle } from "lucide-react";

// Map emoji score value → emoji character
const SCORE_EMOJI_MAP: Record<number, string> = {
  1: "😠",
  2: "😕",
  3: "😐",
  4: "🙂",
  5: "😄",
};

const SCORE_BORDER_COLOR: Record<number, string> = {
  1: "text-red-400",
  2: "text-orange-400",
  3: "text-yellow-400",
  4: "text-green-400",
  5: "text-teal-400",
};

/** Score band. A stable id, so the colour map and the translated label
 *  cannot drift apart — previously both were keyed off the English text. */
type ScoreBand = "excellent" | "good" | "average" | "poor";

const BAND_CLASS: Record<ScoreBand, string> = {
  excellent: "bg-green-100 text-green-700 border-green-300",
  good: "bg-info-muted text-info border-blue-300",
  average: "bg-yellow-50 text-yellow-600 border-yellow-300",
  poor: "bg-red-100 text-red-600 border-red-300",
};

const BAND_LABEL: Record<ScoreBand, string> = {
  excellent: "company:performance_review.band_excellent",
  good: "company:performance_review.band_good",
  average: "company:performance_review.band_average",
  poor: "company:performance_review.band_poor",
};

const bandFromPercent = (percent: number): ScoreBand => {
  if (percent >= 90) return "excellent";
  if (percent >= 75) return "good";
  if (percent >= 50) return "average";
  return "poor";
};

interface ResultSummaryProps {
  overallPercent: number;
  totalWeightedScore: number;
  metrics: IEvaluationRequestDetailSummaryMetric[];
}

export const ResultSummary = ({
  overallPercent,
  totalWeightedScore,
  metrics,
}: ResultSummaryProps) => {
  const { t } = useTranslation(["common", "company"]);
  const band = bandFromPercent(overallPercent);

  const totalWeightage = metrics?.reduce(
    (sum, m) => sum + (m.weightage ?? 0),
    0,
  );

  const totalScore = metrics?.reduce(
    (sum, m) => sum + (m.weightedScore ?? 0),
    0,
  );

  return (
    <div className="flex flex-col h-full bg-white">
      {/* Panel title */}
      <div className="px-5 py-4 border-b border-gray-100">
        <h2 className="text-base font-semibold text-gray-800">
          {t("company:performance_review.result_summary")}
        </h2>
      </div>

      {/* Score ring */}
      <div className="flex flex-col items-center gap-3 py-6 border-gray-100">
        <OverallScoreRing percent={overallPercent} />

        <Badge
          className={`rounded-xs font-medium border px-3 py-0.5 ${BAND_CLASS[band]}`}
        >
          {t(BAND_LABEL[band])}
        </Badge>
      </div>

      {/* Metric breakdown */}
      <div className="flex-1 px-4 py-4 flex flex-col gap-1 overflow-y-auto">
        <p className="text-xs font-semibold text-gray-500 mb-2 border-b pb-1">
          {t("company:performance_review.metric_breakdown")}
        </p>

        {metrics?.length > 0 ? (
          metrics.map((metric) => (
            <MetricBreakdownRow key={metric.metricId} metric={metric} />
          ))
        ) : (
          <div className="flex flex-col items-center justify-center gap-2 py-6 text-gray-400 min-h-[200px]">
            <p className="text-xs">
              {t("company:performance_review.no_breakdown_available")}
            </p>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className=" border-t  flex flex-col justify-between">
        <div className="px-2 py-3 flex items-center justify-between border-b">
          <span className="text-sm font-semibold text-gray-700">
            {t("company:performance_review.total_weighted_score")}
          </span>
          <span className="text-sm font-bold text-primary">
            {totalScore}/{totalWeightage}
          </span>
        </div>
        <div className="px-2 py-3 flex items-center justify-between">
          <span className="text-sm font-semibold text-gray-700">
            {t("company:performance_review.total_weighted_percentage")}
          </span>
          <span className="text-sm font-bold text-primary">
            {totalWeightedScore}/100
          </span>
        </div>
      </div>
    </div>
  );
};

// ─── Metric Breakdown Row ─────────────────────────────────────────────────────

const MetricBreakdownRow = ({
  metric,
}: {
  metric: IEvaluationRequestDetailSummaryMetric;
}) => {
  const { t } = useTranslation(["company"]);
  const emoji = SCORE_EMOJI_MAP[metric.score] ?? "—";
  const borderColor = SCORE_BORDER_COLOR[metric.score] ?? "text-border";

  return (
    <div className="flex items-center justify-between py-2 border-gray-50 last:border-0">
      <div className="flex items-center gap-2">
        <div
          className={cn(
            "relative flex items-center justify-center rounded-full",
            borderColor,
          )}
          style={{ width: 42, height: 42 }}
        >
          <Circle
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
            strokeWidth={0.75}
            size={42}
          />
          <span className="text-lg leading-none flex items-center justify-center">
            {emoji}
          </span>
        </div>
        <div>
          <p className="text-xs font-medium text-gray-800">
            {metric.title ?? "-"}
          </p>
          <p className="text-[11px] text-gray-400">
            {t("company:performance_review.weightage_plain", {
              value: metric.weightage ?? "-",
            })}
          </p>
        </div>
      </div>

      <span className="text-xs font-semibold text-black">
        {t("company:performance_review.score_plain", {
          value: metric.weightedScore ?? "-",
        })}
      </span>
    </div>
  );
};
